import React, { useMemo } from 'react';
import { Dayjs } from 'dayjs';
import styled from 'styled-components';

interface TimelineBackgroundBandsProps {
  endDate: Dayjs;
  startDate: Dayjs;
}

const TimelineBackgroundBands: React.FC<TimelineBackgroundBandsProps> = ({
  startDate,
  endDate,
}) => {
  // Generate year markers for background bands (matching header logic)
  const yearMarkers = useMemo(() => {
    const markers: { end: Dayjs; start: Dayjs; year: number }[] = [];
    let currentYear = startDate.year();
    const endYear = endDate.year();

    while (currentYear <= endYear) {
      const yearStart = startDate.year(currentYear).startOf('year');
      const yearEnd = startDate.year(currentYear).endOf('year');

      // Clamp to the actual timeline bounds
      const actualStart = yearStart.isBefore(startDate) ? startDate : yearStart;
      const actualEnd = yearEnd.isAfter(endDate) ? endDate : yearEnd;

      markers.push({
        year: currentYear,
        start: actualStart,
        end: actualEnd,
      });

      currentYear += 1;
    }

    return markers;
  }, [startDate, endDate]);

  const totalDays = endDate.diff(startDate, 'days');

  return (
    <BackgroundContainer>
      {/* Year boundary lines */}
      <YearBoundaryLines>
        {yearMarkers.slice(1).map((yearData: { end: Dayjs; start: Dayjs; year: number }) => {
          const daysSinceStart = yearData.start.diff(startDate, 'days');
          const leftPercent = (daysSinceStart / totalDays) * 100;

          return <YearBoundaryLine key={yearData.year} left={leftPercent} />;
        })}
      </YearBoundaryLines>
    </BackgroundContainer>
  );
};

const BackgroundContainer = styled.div`
  position: absolute;
  top: -2px;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;

  /* Ensure no borders or outlines */
  border: none;
  outline: none;
  box-shadow: none;
`;

const YearBoundaryLines = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
`;

const YearBoundaryLine = styled.div<{ left: number }>`
  position: absolute;
  left: ${({ left }) => left}%;
  top: -2px;
  bottom: 0;
  width: 2.25px;
  background-image: linear-gradient(#afafaf 33%, rgba(255, 255, 255, 0) 0%);
  background-size: 2.25px 12px;
  background-repeat: repeat-y;
  z-index: 10;
`;

export { TimelineBackgroundBands };
